/**
 * Copyright (c) Unic AG
 *
 * multilanguage functions to create multi-domain routing for production or single domain routing for development.
 *
 * @summary create routing URI
 * @author Tobias.Frei <tobias.frei@unic.com>
 */

import { IntlShape } from 'react-intl';
import { supportedLanguages } from '../../core/utils/i18n/index';
import { getRootTranslation } from '../../../gatsby-helper';

const isDev = process.env.NODE_ENV === 'development';
interface Domains {
  [key: string]: string;
}

// change domains here
const domains: Domains = {
  de: `https://${process.env.GATSBY_URL_PREFIX}laden-punkt.ch`,
  it: `https://${process.env.GATSBY_URL_PREFIX}ricarica-punto.ch`,
  fr: `https://${process.env.GATSBY_URL_PREFIX}recharge-au-point.ch`,
};

export const getDomainForLocale = (locale: string) => {
  return domains[locale];
};

/**
 * URI generator
 *
 * provide production and dev routing URI
 *
 * @author    tobias.frei
 *
 * @param {object}    params          hub data
 *
 * @return {string} URI
 */
export const createRoutingURI = (params: {
  root: string;
  slug?: string;
  locale: string;
}): string => {
  const { root, slug, locale } = params;

  let url = root.length > 1 ? `${root}` : '';
  url = slug && slug.length > 1 ? `${url}/${slug}` : url;
  // force trailing slashes
  const link = isDev ? `/${locale}/${url}` : `/${locale}/${url}/`;
  return link.replace(/([^:]\/)\/+/g, '$1');
};

type createLanguageObjectProps = {
  /** language object */
  intl: IntlShape;
  /** root string */
  root: string;
  /** slug string */
  slug: string;
};

/**
 * language object creator
 *
 * create language object for static pages
 *
 * @author    tobias.frei
 *
 * @param {object}    intl        language object by createLanguageObject()
 * @param {string}    root        actual root folder
 * @param {string}    slug        actual slug
 *
 * @return {string} full language URI
 */
export const createLanguageObject = ({
  intl,
  root,
  slug,
}: createLanguageObjectProps) => {
  return {
    switcher: supportedLanguages.map(
      ({ languageTag }: { languageTag: string }) => ({
        node_locale: languageTag,
        root: getRootTranslation(root, languageTag),
        slug,
      }),
    ),
    actual: intl.locale,
  };
};

type createLanguagePathProps = {
  /** language switcher object */
  switcher: [{ node_locale: string; root: string; slug: string }];
  /** acutal language string */
  language: string;
};

/**
 * language path creator
 *
 * create language path for hreflang and language switch
 *
 * @author    tobias.frei
 *
 * @param {object}    switcher    language object by createLanguageObject()
 * @param {string}    folder      actual root slug
 * @param {object}    language    actual language
 *
 * @return {string} language path
 */
export const createLanguagePath = ({
  switcher,
  language,
}: createLanguagePathProps): string => {
  const langObj = switcher.filter(
    ({ node_locale }: { node_locale: string }) => node_locale === language,
  )[0];

  if (!langObj) return '/';

  const url = createRoutingURI(getRootSlug(langObj, langObj.node_locale));

  return isDev ? url : getDomainForLocale(langObj.node_locale) + url;
};

/**
 * root getter
 *
 * provides campaign root slug
 *
 * @author    tobias.frei
 *
 * @param {object}    item          page object
 * @param {string}    node_locale   actual lang
 *
 * @return {string} root slug
 */
export const getRootSlug = (
  item: {
    __typename?: string;
    slug?: string;
    root?: string;
    defaultHub?: { slug: string };
  },
  node_locale: string,
) => {
  const { __typename, slug, root } = item;
  let finalRootSlug = root || '';
  let finalSlug = slug || '';

  switch (__typename) {
    case 'ContentfulCampaignConfiguration': {
      const mainHubSlug = item.defaultHub?.slug;
      finalSlug = mainHubSlug ? mainHubSlug : '';
      break;
    }
    case 'ContentfulNewsHub':
      finalRootSlug = getRootTranslation('news', node_locale);
      break;
    case 'ContentfulToolHub':
      finalRootSlug = getRootTranslation('tools', node_locale);
      break;
    case 'ContentfulStoryHub':
      finalRootSlug = getRootTranslation('stories', node_locale);
      break;
    case 'ContentfulEventHub':
      finalRootSlug = getRootTranslation('events', node_locale);
      break;
    case 'ContentfulFundingHub':
      finalRootSlug = getRootTranslation('fundings', node_locale);
      break;
    case 'ContentfulConsultationHub':
      finalRootSlug = getRootTranslation('consultations', node_locale);
      break;
    default:
      break;
  }

  return {
    slug: finalSlug,
    root: finalRootSlug,
    locale: node_locale,
  };
};
